import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

// Determine the API URL based on environment
const getApiUrl = () => {
  // Check if we're in production by looking at the hostname
  const hostname = window.location.hostname;
  
  // Production
  if (hostname === 'x-aws.com' || hostname === 'www.x-aws.com') {
    return 'https://api.x-aws.com/api';
  }
  
  // Development
  return `http://${hostname}:3000/api`;
};

// Create axios instance with default config
const api: AxiosInstance = axios.create({
  timeout: 30000, // Default timeout for most requests
  headers: {
    'Content-Type': 'application/json',
  },
  // Prevent axios from using relative URLs
  adapter: axios.defaults.adapter,
});

// Set the base URL once
api.defaults.baseURL = getApiUrl();
console.log('API configured with base URL:', api.defaults.baseURL);

// Request interceptor for auth
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      data: error.response?.data,
      message: error.message
    });
    
    if (error.response?.status === 401) {
      // Handle unauthorized access
      const isAdminRoute = error.config?.url?.includes('/admin');
      const isMSPRoute = error.config?.url?.includes('/msp');
      const isAdminUser = localStorage.getItem('isAdmin') === 'true';
      
      // Don't redirect if it's an MSP route - let the MSP components handle the error
      if (isMSPRoute) {
        return Promise.reject(error);
      }
      
      if (isAdminRoute || isAdminUser) {
        // For admin routes or admin users, redirect to admin login
        localStorage.removeItem('authToken');
        localStorage.removeItem('isAdmin');
        window.location.href = '/admin/login';
      } else {
        // For regular users, redirect to regular login
        localStorage.removeItem('authToken');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Generic API methods
export const apiService = {
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    api.get<T>(url, config),
  
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    api.post<T>(url, data, config),
  
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    api.put<T>(url, data, config),
  
  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    api.patch<T>(url, data, config),
  
  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    api.delete<T>(url, config),
};

export default api;